import { getAccountNumberError, getRoutingNumberError, isInternalUser } from '@steerfi/shared';
import { AlertCircle } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { AddressVerificationResponse, StandardizedAddress } from '@/api/address';
import { type ClientData, getClients } from '@/api/clients';
import {
  type CreateIndividualData,
  createIndividual,
  getSupplierAddConfig,
  type SupplierAddType,
} from '@/api/suppliers';
import {
  AddressFormFields,
  AddressVerificationDialog,
  type AddressVerificationStatus,
} from '@/components/address';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useSupplierDisplay } from '@/contexts/supplier-display-context';
import { getFieldValidationError } from '@/utils/field-validators';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';

// ── Individual form types ───────────────────────────────────────────────

type PaymentMethod = 'ACH' | 'CHECK' | 'DIGITAL_WALLET';

interface IndividualFormData {
  firstName: string;
  lastName: string;
  email: string;
  caseId: string;
  street: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  addressVerificationStatus: AddressVerificationStatus;
  paymentMethod: PaymentMethod | '';
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankAccountType: 'CHECKING' | 'SAVINGS';
}

interface FormErrors {
  [key: string]: string;
}

const INITIAL_INDIVIDUAL_FORM: IndividualFormData = {
  firstName: '',
  lastName: '',
  email: '',
  caseId: '',
  street: '',
  address2: '',
  city: '',
  state: '',
  zipCode: '',
  addressVerificationStatus: 'not_checked',
  paymentMethod: '',
  bankRoutingNumber: '',
  bankAccountNumber: '',
  bankAccountType: 'CHECKING',
};

// ── Component ───────────────────────────────────────────────────────────

interface SupplierAddModalProps {
  open: boolean;
  onClose: () => void;
  onAddSuccess?: () => void;
  partyId: string;
  /** When provided, reuses the same config as the page type filter (no fetch). */
  allowedTypes?: SupplierAddType[];
  /** Optional preselected client context for internal "on behalf of" workflows. */
  actingOnBehalfClient?: { id: string; name: string };
}

const DEFAULT_ALLOWED_TYPES: SupplierAddType[] = ['individual', 'business'];

export const SupplierAddModal: React.FC<SupplierAddModalProps> = ({
  open,
  onClose,
  onAddSuccess,
  partyId,
  allowedTypes: allowedTypesProp,
  actingOnBehalfClient,
}) => {
  const { labels } = useSupplierDisplay();
  const { user } = useKeycloakAuth();
  const isInternal = isInternalUser(user?.roles);
  const [allowedTypesState, setAllowedTypesState] =
    useState<SupplierAddType[]>(DEFAULT_ALLOWED_TYPES);
  const allowedTypes = allowedTypesProp?.length ? allowedTypesProp : allowedTypesState;
  const [supplierType, setSupplierType] = useState<SupplierAddType>('individual');

  // ── Client selector state (internal users only) ────────────────────────
  const [clients, setClients] = useState<ClientData[]>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(actingOnBehalfClient?.id ?? '');
  const [clientsLoading, setClientsLoading] = useState(false);
  const isClientSelectionLocked = Boolean(actingOnBehalfClient);
  const effectiveSelectedClientId = actingOnBehalfClient?.id || selectedClientId;

  const filteredClients = useMemo(
    () =>
      clientSearch
        ? clients.filter((c) => c.name.toLowerCase().includes(clientSearch.toLowerCase()))
        : clients,
    [clients, clientSearch]
  );

  // Fetch clients when modal opens for internal users
  useEffect(() => {
    if (!open || !isInternal || isClientSelectionLocked) return;
    setClientsLoading(true);
    getClients({ limit: 100, sortBy: 'name', order: 'asc' })
      .then((res) => setClients(res.data))
      .catch(() => setClients([]))
      .finally(() => setClientsLoading(false));
  }, [open, isInternal, isClientSelectionLocked]);

  useEffect(() => {
    if (actingOnBehalfClient?.id) {
      setSelectedClientId(actingOnBehalfClient.id);
    }
  }, [actingOnBehalfClient?.id]);

  // Fetch allowed types only when not provided by parent (e.g. when modal used outside suppliers page)
  useEffect(() => {
    if (allowedTypesProp != null || !open || !partyId) return;
    getSupplierAddConfig(partyId)
      .then((config) => {
        const types = config.allowedTypes?.length ? config.allowedTypes : DEFAULT_ALLOWED_TYPES;
        setAllowedTypesState(types);
        setSupplierType((current) => (types.includes(current) ? current : types[0]));
      })
      .catch(() => setAllowedTypesState(DEFAULT_ALLOWED_TYPES));
  }, [open, partyId, allowedTypesProp]);

  // Sync supplierType when allowedTypes (from prop or state) changes
  useEffect(() => {
    if (!allowedTypes.includes(supplierType)) {
      setSupplierType(allowedTypes[0]);
    }
  }, [allowedTypes, supplierType]);

  // ── Individual state ──────────────────────────────────────────────────
  const [indForm, setIndForm] = useState<IndividualFormData>(INITIAL_INDIVIDUAL_FORM);
  const [indErrors, setIndErrors] = useState<FormErrors>({});
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [addressVerificationResult, setAddressVerificationResult] =
    useState<AddressVerificationResponse | null>(null);

  // ── Business state ────────────────────────────────────────────────────
  const [bizName, setBizName] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [bizPaymentMethod, setBizPaymentMethod] = useState('');
  const [bizDescription, setBizDescription] = useState('');
  const [bizErrors, setBizErrors] = useState<FormErrors>({});

  // ── Shared state ──────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Inline validation (runs on every keystroke) ─────────────────────
  const getIndFieldError = (field: keyof IndividualFormData, value: string): string | null =>
    getFieldValidationError(field, value, indForm.paymentMethod);

  // ── Individual helpers ────────────────────────────────────────────────
  const updateIndField = (field: keyof IndividualFormData, value: string | boolean) => {
    setIndForm((prev) => ({ ...prev, [field]: value }));
    if (
      indForm.addressVerificationStatus !== 'not_checked' &&
      'street address2 city state zipCode'.includes(String(field))
    ) {
      setIndForm((prev) => ({ ...prev, addressVerificationStatus: 'not_checked' }));
    }
    if (typeof field === 'string') {
      const error = getIndFieldError(field as keyof IndividualFormData, String(value));
      setIndErrors((prev) => {
        const next = { ...prev };
        if (error) {
          next[field] = error;
        } else {
          delete next[field];
        }
        return next;
      });
    }
  };

  const handleAddressChange = (
    field: 'address1' | 'address2' | 'city' | 'state' | 'zipCode',
    value: string
  ) => {
    const map: Record<string, keyof IndividualFormData> = {
      address1: 'street',
      address2: 'address2',
      city: 'city',
      state: 'state',
      zipCode: 'zipCode',
    };
    updateIndField(map[field], value);
  };

  const handleAddressVerificationResult = (result: AddressVerificationResponse) => {
    setAddressVerificationResult(result);
    if (result.hasCorrections || !result.verified) {
      setShowAddressDialog(true);
    }
  };

  const handleAcceptSuggestedAddress = (suggested: StandardizedAddress) => {
    setIndForm((prev) => ({
      ...prev,
      street: suggested.streetAddress,
      address2: suggested.secondaryAddress ?? '',
      city: suggested.city,
      state: suggested.state,
      zipCode: suggested.zipCode,
      addressVerificationStatus: 'verified',
    }));
    setShowAddressDialog(false);
  };

  const handleKeepOriginalAddress = () => {
    setIndForm((prev) => ({
      ...prev,
      addressVerificationStatus: addressVerificationResult?.verified ? 'verified' : 'not_verified',
    }));
    setShowAddressDialog(false);
  };

  const validateIndividual = (): boolean => {
    const newErrors: FormErrors = {};

    if (isInternal && !effectiveSelectedClientId) newErrors.client = 'Client is required';
    if (!indForm.caseId.trim()) newErrors.caseId = 'Case ID is required';
    if (!indForm.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (!/[a-zA-Z]/.test(indForm.firstName)) {
      newErrors.firstName = 'First name must contain at least one letter';
    }
    if (!indForm.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (!/[a-zA-Z]/.test(indForm.lastName)) {
      newErrors.lastName = 'Last name must contain at least one letter';
    }
    if (!indForm.paymentMethod) newErrors.paymentMethod = 'Payment method is required';

    if (!indForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(indForm.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (indForm.paymentMethod === 'ACH') {
      const routingError = getRoutingNumberError(indForm.bankRoutingNumber);
      if (routingError) newErrors.bankRoutingNumber = routingError;
      const accountError = getAccountNumberError(indForm.bankAccountNumber);
      if (accountError) newErrors.bankAccountNumber = accountError;
    }

    if (!indForm.street.trim()) {
      newErrors.street = 'Street address is required';
    } else if (indForm.street.includes('@')) {
      newErrors.street = 'Please enter a valid street address';
    }
    if (!indForm.city.trim()) {
      newErrors.city = 'City is required';
    } else if (!/[a-zA-Z]/.test(indForm.city)) {
      newErrors.city = 'City must contain at least one letter';
    }
    if (!indForm.state) newErrors.state = 'State is required';
    if (!indForm.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(indForm.zipCode.trim())) {
      newErrors.zipCode = 'ZIP code must be 5 digits or 5+4 format (e.g., 10001-1234)';
    }

    setIndErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBusiness = (): boolean => {
    const newErrors: FormErrors = {};
    if (!bizName.trim()) newErrors.bizName = 'Business name is required';
    if (!bizEmail.trim()) {
      newErrors.bizEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(bizEmail)) {
      newErrors.bizEmail = 'Invalid email format';
    }
    if (!bizPaymentMethod) newErrors.bizPaymentMethod = 'Payment method is required';
    setBizErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Reset ─────────────────────────────────────────────────────────────
  const resetForm = () => {
    setSupplierType(allowedTypes[0] ?? 'individual');
    setIndForm(INITIAL_INDIVIDUAL_FORM);
    setIndErrors({});
    setShowAddressDialog(false);
    setAddressVerificationResult(null);
    setBizName('');
    setBizEmail('');
    setBizPaymentMethod('');
    setBizDescription('');
    setBizErrors({});
    setSelectedClientId(actingOnBehalfClient?.id ?? '');
    setClientSearch('');
    setError(null);
  };

  // ── Submit ────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    setError(null);

    if (supplierType === 'individual') {
      if (!validateIndividual()) return;

      setIsSubmitting(true);
      try {
        const payload: CreateIndividualData = {
          first_name: indForm.firstName.trim(),
          last_name: indForm.lastName.trim(),
          email: indForm.email.trim(),
          case_id: indForm.caseId.trim(),
          payment_method: indForm.paymentMethod as PaymentMethod,
          street: indForm.street.trim(),
          city: indForm.city.trim(),
          state: indForm.state,
          zip_code: indForm.zipCode.trim(),
        };

        const line2 = indForm.address2.trim();
        if (line2) {
          payload.address2 = line2;
        }

        if (isInternal && effectiveSelectedClientId) {
          payload.party_id = effectiveSelectedClientId;
        }

        payload.address_verification_status = indForm.addressVerificationStatus;

        if (indForm.paymentMethod === 'ACH') {
          payload.bank_routing_number = indForm.bankRoutingNumber.trim();
          payload.bank_account_number = indForm.bankAccountNumber.trim();
          payload.bank_account_type = indForm.bankAccountType;
        }

        await createIndividual(payload);

        toast.success(`${labels.singular} created successfully`, {
          description: `${indForm.firstName} ${indForm.lastName} has been added as a ${labels.singular.toLowerCase()}.`,
        });
        resetForm();
        onAddSuccess?.();
        onClose();
      } catch (err: unknown) {
        const rawMessage = err instanceof Error ? err.message : 'Failed to create individual';
        const message = /case id.*already been created/i.test(rawMessage)
          ? 'That case ID has already been created.'
          : rawMessage;
        setError(message);
        toast.error(`Failed to create ${labels.singular.toLowerCase()}`, { description: message });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!validateBusiness()) return;
      toast.info(`Business ${labels.singular.toLowerCase()} creation is not yet implemented.`);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const isClientSelected = !isInternal || !!effectiveSelectedClientId;
  const isIndividualValid =
    isClientSelected &&
    indForm.caseId.trim() &&
    indForm.firstName.trim() &&
    indForm.lastName.trim() &&
    indForm.email.trim() &&
    indForm.paymentMethod &&
    indForm.street.trim() &&
    indForm.city.trim() &&
    indForm.state &&
    indForm.zipCode.trim() &&
    (indForm.paymentMethod !== 'ACH' ||
      (indForm.bankRoutingNumber.trim() && indForm.bankAccountNumber.trim()));
  const isBusinessValid = bizName.trim() && bizEmail.trim() && bizPaymentMethod;
  const canSubmit = supplierType === 'individual' ? isIndividualValid : isBusinessValid;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] flex flex-col gap-0 p-0">
        {/* ── Sticky header ──────────────────────────────────────────── */}
        <div className="shrink-0 border-b px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle>Add {labels.singular}</DialogTitle>
            <DialogDescription>
              Enter the {labels.singular.toLowerCase()}&apos;s details below to add them to your
              account.
            </DialogDescription>
          </DialogHeader>

          {allowedTypes.length > 1 ? (
            <Tabs
              value={supplierType}
              onValueChange={(v) => setSupplierType(v as SupplierAddType)}
              className="mt-4"
            >
              <TabsList className="grid w-full grid-cols-2">
                {allowedTypes.includes('individual') && (
                  <TabsTrigger value="individual">{labels.singular}</TabsTrigger>
                )}
                {allowedTypes.includes('business') && (
                  <TabsTrigger value="business">Business</TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          ) : null}
        </div>

        {/* ── Scrollable body ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {supplierType === 'individual' ? (
            <div className="space-y-5">
              {/* Client selector for internal users */}
              {isInternal && (
                <>
                  <SectionHeader first>Client</SectionHeader>
                  {isClientSelectionLocked ? (
                    <FormField
                      id="ind-client-locked"
                      label="Client"
                      required
                      error={indErrors.client}
                    >
                      <Input
                        id="ind-client-locked"
                        value={actingOnBehalfClient?.name ?? ''}
                        disabled
                        readOnly
                      />
                    </FormField>
                  ) : (
                    <FormField id="ind-client" label="Client" required error={indErrors.client}>
                      <Select
                        value={selectedClientId}
                        onValueChange={(v) => {
                          setSelectedClientId(v);
                          if (indErrors.client) {
                            setIndErrors((prev) => {
                              const next = { ...prev };
                              delete next.client;
                              return next;
                            });
                          }
                        }}
                        disabled={isSubmitting || clientsLoading}
                      >
                        <SelectTrigger id="ind-client" className="w-full">
                          <SelectValue
                            placeholder={clientsLoading ? 'Loading clients...' : 'Select a client'}
                          />
                        </SelectTrigger>
                        <SelectContent onCloseAutoFocus={() => setClientSearch('')}>
                          <div className="px-2 pb-1.5">
                            <Input
                              placeholder="Search clients..."
                              value={clientSearch}
                              onChange={(e) => setClientSearch(e.target.value)}
                              className="h-8"
                              onKeyDown={(e) => e.stopPropagation()}
                            />
                          </div>
                          {filteredClients.length === 0 ? (
                            <div className="py-2 px-2 text-sm text-muted-foreground">
                              No clients found
                            </div>
                          ) : (
                            filteredClients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormField>
                  )}
                </>
              )}

              {/* Individual Information — Case ID at top */}
              <SectionHeader first={!isInternal}>{labels.singular} Information</SectionHeader>
              <FormField id="ind-case-id" label="Case ID" required error={indErrors.caseId}>
                <Input
                  id="ind-case-id"
                  placeholder="Case ID"
                  value={indForm.caseId}
                  onChange={(e) =>
                    updateIndField(
                      'caseId',
                      e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '')
                    )
                  }
                  disabled={isSubmitting}
                />
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  id="ind-first-name"
                  label="First Name"
                  required
                  error={indErrors.firstName}
                >
                  <Input
                    id="ind-first-name"
                    placeholder="First Name"
                    value={indForm.firstName}
                    onChange={(e) => updateIndField('firstName', e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
                <FormField id="ind-last-name" label="Last Name" required error={indErrors.lastName}>
                  <Input
                    id="ind-last-name"
                    placeholder="Last Name"
                    value={indForm.lastName}
                    onChange={(e) => updateIndField('lastName', e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              </div>

              {/* Payment Method */}
              <FormField
                id="ind-payment-method"
                label="Payment Method"
                required
                error={indErrors.paymentMethod}
              >
                <Select
                  value={indForm.paymentMethod}
                  onValueChange={(v) => updateIndField('paymentMethod', v)}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="ind-payment-method" className="w-full">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACH">ACH</SelectItem>
                    <SelectItem value="CHECK">Check</SelectItem>
                    <SelectItem value="DIGITAL_WALLET">Digital Wallet</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>

              {/* ACH Bank Details */}
              {indForm.paymentMethod === 'ACH' && (
                <div className="space-y-4 rounded-md border p-4 bg-muted/30">
                  <h4 className="text-sm font-semibold text-foreground">Bank Account Details</h4>
                  <FormField id="ind-account-type" label="Account Type">
                    <Select
                      value={indForm.bankAccountType}
                      onValueChange={(v) =>
                        updateIndField('bankAccountType', v as 'CHECKING' | 'SAVINGS')
                      }
                      disabled={isSubmitting}
                    >
                      <SelectTrigger id="ind-account-type" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CHECKING">Checking</SelectItem>
                        <SelectItem value="SAVINGS">Savings</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormField>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      id="ind-routing"
                      label="Bank Routing Number"
                      required
                      error={indErrors.bankRoutingNumber}
                    >
                      <Input
                        id="ind-routing"
                        placeholder="9-digit routing number"
                        value={indForm.bankRoutingNumber}
                        onChange={(e) =>
                          updateIndField('bankRoutingNumber', e.target.value.replace(/\D/g, ''))
                        }
                        disabled={isSubmitting}
                        maxLength={9}
                      />
                    </FormField>
                    <FormField
                      id="ind-account"
                      label="Account Number"
                      required
                      error={indErrors.bankAccountNumber}
                    >
                      <Input
                        id="ind-account"
                        placeholder="Account number"
                        value={indForm.bankAccountNumber}
                        onChange={(e) =>
                          updateIndField('bankAccountNumber', e.target.value.replace(/\D/g, ''))
                        }
                        disabled={isSubmitting}
                        maxLength={17}
                      />
                    </FormField>
                  </div>
                </div>
              )}

              <SectionHeader>Additional Details</SectionHeader>
              <AddressFormFields
                address={{
                  address1: indForm.street,
                  address2: indForm.address2,
                  city: indForm.city,
                  state: indForm.state,
                  zipCode: indForm.zipCode,
                }}
                errors={{
                  address1: indErrors.street,
                  address2: indErrors.address2,
                  city: indErrors.city,
                  state: indErrors.state,
                  zipCode: indErrors.zipCode,
                }}
                onChange={handleAddressChange}
                onVerificationResult={handleAddressVerificationResult}
                onVerificationStatusChange={(status) =>
                  setIndForm((prev) => ({ ...prev, addressVerificationStatus: status }))
                }
                disabled={isSubmitting}
              />
              <FormField id="ind-email" label="Email" required error={indErrors.email}>
                <Input
                  id="ind-email"
                  type="email"
                  placeholder="email@example.com"
                  value={indForm.email}
                  onChange={(e) => updateIndField('email', e.target.value)}
                  disabled={isSubmitting}
                />
              </FormField>
            </div>
          ) : (
            /* ── Business form ──────────────────────────────────────── */
            <div className="space-y-4">
              <FormField id="biz-name" label="Business Name" required error={bizErrors.bizName}>
                <Input
                  id="biz-name"
                  placeholder="Company name"
                  value={bizName}
                  onChange={(e) => setBizName(e.target.value)}
                  disabled={isSubmitting}
                />
              </FormField>
              <FormField id="biz-email" label="Email" required error={bizErrors.bizEmail}>
                <Input
                  id="biz-email"
                  type="email"
                  placeholder="business@example.com"
                  value={bizEmail}
                  onChange={(e) => setBizEmail(e.target.value)}
                  disabled={isSubmitting}
                />
              </FormField>
              <FormField
                id="biz-payment"
                label="Payment Method"
                required
                error={bizErrors.bizPaymentMethod}
              >
                <Select
                  value={bizPaymentMethod}
                  onValueChange={setBizPaymentMethod}
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="biz-payment" className="w-full">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ach">ACH</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="virtual_wallet">Virtual Wallet</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField id="biz-desc" label="Description (Optional)">
                <Textarea
                  id="biz-desc"
                  placeholder="Add notes or description about this business..."
                  value={bizDescription}
                  onChange={(e) => setBizDescription(e.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                />
              </FormField>
            </div>
          )}
        </div>

        {/* ── Sticky footer ──────────────────────────────────────────── */}
        <div className="shrink-0 border-t px-6 py-5 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">
                  Failed to add {labels.singular.toLowerCase()}
                </p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="button" onClick={handleAdd} disabled={!canSubmit || isSubmitting}>
              {isSubmitting
                ? 'Adding...'
                : supplierType === 'individual'
                  ? `Create ${labels.singular}`
                  : `Add ${labels.singular}`}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
      <AddressVerificationDialog
        open={showAddressDialog}
        onOpenChange={setShowAddressDialog}
        verificationResult={addressVerificationResult}
        onAcceptSuggested={handleAcceptSuggestedAddress}
        onKeepOriginal={handleKeepOriginalAddress}
      />
    </Dialog>
  );
};

// ── Helper components ───────────────────────────────────────────────────

const SectionHeader: React.FC<{ children: React.ReactNode; first?: boolean }> = ({
  children,
  first,
}) => (
  <div className={first ? '' : 'border-t pt-5'}>
    <h3 className="text-lg font-semibold tracking-tight text-foreground">{children}</h3>
  </div>
);

const FormField: React.FC<{
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}> = ({ id, label, required, error, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
    {children}
    {error && <p className="text-xs text-red-600">{error}</p>}
  </div>
);
